import React, { useState, useEffect } from 'react';
import get from 'lodash/get';
import cn from 'classnames';
import Collapse, { Panel } from 'components/Collapse';
import Header from 'views/pages/ClientDetail/CompanyOfficial/components/Header';
import NotFound from 'views/pages/ClientDetail/CompanyOfficial/components/NotFound';
import StatusTag from 'views/pages/ClientDetail/CompanyOfficial/components/StatusTag';
import PanelHeader from 'views/pages/ClientDetail/CompanyOfficial/PanelHeader';
import {
  isAlreadyNew,
  afterCbFn,
} from 'views/pages/ClientDetail/CompanyOfficial/utils';
import {
  CollapseTable,
  PrimaryTag,
} from 'views/pages/ClientDetail/components/Elements';
import {
  onKeyChangeFn,
  getEditKeys,
  editChangeFn,
  getIsKey,
} from 'views/pages/ClientDetail/utils';
import Section from './components/Section';
import { useActions, useIndexData } from './selectorData';
import { DEFAULT_SECTIONS, NEW_KEY } from './constants';
import { getLocationBR } from './utils';
import classes from './Locations.module.scss';

const Locations = ({
  className,
  confirmModal,
  openWarningModal,
  clientId,
  setIsEdit,
  setIsFormChanged,
  activeTab,
  tabIndex,
  status,
  setStatus,
  tabName,
  onUpdated,
}) => {
  const {
    getLocations,
    createUnderwritingRecord,
    updateUnderwritingRecord,
    updateUnderwritingRecordStatus,
    syncSetUnderwritingPrimaryContact,
  } = useActions();
  const { locations, updateLocationLoading } = useIndexData();
  const setIsEditParent = setIsEdit;
  const [createLoading, setCreateLoading] = useState(false);
  const [sections, setSections] = useState(DEFAULT_SECTIONS);
  const [statusLoading, setStatusLoading] = useState({});
  const [activeKeys, setActiveKeys] = useState([]);
  const [editKeys, setEditKeys] = useState([]);
  const hasEdit = sections.length > 0 || editKeys.length > 0;
  const [editChanged, setEditChanged] = useState({});
  const notFound = sections.length === 0 && locations.length === 0;
  const handleLoadLocation = (loadStatus, cb) => {
    const payloadData = {};
    const payloadStatus = loadStatus || status;
    if (payloadStatus !== 'all') {
      payloadData.status = payloadStatus;
    }
    getLocations({ id: clientId }, payloadData, res => {
      if (res && cb) cb();
    });
  };
  const handleEditChange = (...params) =>
    editChangeFn(editChanged, setEditChanged, setIsEdit, ...params);
  const handleSubmit = id => (values, cb) => {
    const locationId = id && id !== NEW_KEY ? id : '';
    const actionFn = locationId
      ? updateUnderwritingRecord
      : createUnderwritingRecord;
    if (!locationId) {
      setCreateLoading(true);
    }
    actionFn(
      clientId,
      getLocationBR(values, locationId),
      afterCbFn(
        res => {
          if (onUpdated) onUpdated();
          const latestLocationId = get(res, 'data.id', '');
          const setPrimaryContact =
            values.is_primary && values.is_primary.length > 0;
          if (setPrimaryContact && latestLocationId) {
            syncSetUnderwritingPrimaryContact(latestLocationId, tabName);
          }
          if (!locationId) {
            handleLoadLocation(null, () => {
              setCreateLoading(false);
              if (cb) cb();
            });
          } else if (cb) cb();
        },
        () => {
          if (!locationId) setCreateLoading(false);
        }
      ),
      !locationId ? tabName : locationId,
      tabName
    );
  };
  const handleSetStatusLoading = (id, val) => {
    setStatusLoading({
      ...statusLoading,
      [id]: val,
    });
  };
  const handleStatusChange = id => newStatus => {
    handleSetStatusLoading(id, true);
    updateUnderwritingRecordStatus(
      clientId,
      { status: newStatus },
      afterCbFn(
        () => {
          if (onUpdated) onUpdated();
          handleLoadLocation(null, () => {
            handleSetStatusLoading(id, false);
            setActiveKeys(activeKeys.filter(key => String(key) !== String(id)));
          });
        },
        () => handleSetStatusLoading(id, false)
      ),
      id,
      tabName
    );
  };
  const handleListStatusChange = newStatus => {
    handleLoadLocation(newStatus);
  };

  useEffect(() => {
    setIsEditParent(hasEdit);
    setIsFormChanged(hasEdit);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sections, editKeys]);
  useEffect(() => {
    if (String(activeTab) !== String(tabIndex) && hasEdit) {
      if (sections.length > 0) {
        setSections(DEFAULT_SECTIONS);
      }
      if (editKeys.length > 0) {
        setEditKeys([]);
      }
    }
    if (String(activeTab) === String(tabIndex)) {
      handleLoadLocation();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  return (
    <div className={cn(classes.wrapper, className)}>
      <Header
        title="Business Locations"
        status={status}
        setStatus={val => {
          setStatus(val);
          handleListStatusChange(val);
        }}
        onAdd={() => {
          if (!isAlreadyNew(sections)) {
            setSections([
              { label: 'Business Address', id: NEW_KEY, isNew: true },
              ...sections,
            ]);
            setEditKeys([...editKeys, NEW_KEY]);
            setActiveKeys([...activeKeys, NEW_KEY]);
          }
        }}
        className={classes.header}
      />
      {notFound ? (
        <NotFound />
      ) : (
        <div className={classes.content}>
          <CollapseTable
            hideHeader
            section
            className={classes.sectionTable}
            hasPrimary
          >
            <Collapse
              destroyInactivePanel
              onChange={keys =>
                onKeyChangeFn(keys, activeKeys, editKeys, setActiveKeys)
              }
              activeKey={activeKeys}
            >
              {[...sections, ...locations].map(section => {
                const { isNew, id, is_primary } = section;
                const key = String(id);
                const label = 'Business Address';
                const isActive = getIsKey(activeKeys, key);
                const isEdit = getIsKey(editKeys, key);
                const sectionInnerProps = {
                  clientId,
                  rootKey: key,
                  isEdit,
                  isActive,
                  confirmModal,
                  setIsEdit: edit => {
                    const newEditKeys = getEditKeys(editKeys, edit, key);
                    setEditKeys(newEditKeys);
                    if (!edit) {
                      handleEditChange(key, false, newEditKeys);
                    }
                    if (isNew && !edit) {
                      setSections(DEFAULT_SECTIONS);
                    }
                  },
                  data: section,
                  onFormChange: () => {
                    handleEditChange(key, true, editKeys);
                  },
                  openWarningModal,
                  loading: createLoading || updateLocationLoading[id],
                  statusLoading: statusLoading[id],
                  onSubmit: handleSubmit(id),
                  onStatusChange: handleStatusChange(id),
                  is_primary,
                };
                const pHeaderProps = {
                  isEdit,
                  label,
                  isActive,
                  className: cn(classes.panelHeader),
                };
                return (
                  <Panel
                    forceRender={true}
                    className={classes.panel}
                    header={<PanelHeader {...pHeaderProps} />}
                    key={key}
                    extra={
                      !isActive ? (
                        <div className={classes.flex}>
                          {!!is_primary && <PrimaryTag isBig />}
                          <StatusTag status={section.status} />
                        </div>
                      ) : null
                    }
                  >
                    <Section {...sectionInnerProps} />
                  </Panel>
                );
              })}
            </Collapse>
          </CollapseTable>
        </div>
      )}
    </div>
  );
};

export default Locations;
